/**
 * The `tracking` object a quiz submission carries for the backend's
 * quiz_generator_tracking: one entry per answered question, in quiz order,
 * with what the page already holds. Unanswered questions are left out (the
 * backend refuses an empty answer). Time per question is not measured, so
 * none is sent.
 */

function isTextQuestion(q) {
  return q.interaction_kind === 'essay_choice_group' || q.is_essay === true || Boolean(q.pearsonWritingItem);
}

function isMultipleChoice(q) {
  return q.multiple_choice !== false && Array.isArray(q.options) && q.options.length > 1;
}

function hasText(ans) {
  return Boolean(String(ans?.text_response || '').trim());
}

function answeredYesNo(ans) {
  if (Array.isArray(ans.yes_no)) return ans.yes_no.some((item) => item?.yes_no_item_id);
  return Object.keys(ans.yes_no_answers || {}).length > 0;
}

function answeredMostLeast(ans) {
  const most = ans.most_option_id || ans.most_least_answers?.most_option_id;
  const least = ans.least_option_id || ans.most_least_answers?.least_option_id;
  return Boolean(most && least);
}

function orNull(value) {
  return value === undefined || value === null || value === '' ? null : String(value);
}

function trackedAnswer(q, ans, position) {
  const base = {
    position,
    question_id: orNull(ans.question_db_id || q.db_id),
    question_topic: orNull(q.topic || q.topic_name),
    question_parameter: orNull(q.parameter),
  };
  if (isTextQuestion(q)) {
    return hasText(ans) ? { ...base, question_type: 'text', text_response: String(ans.text_response) } : null;
  }
  if (q.interaction_kind === 'yes_no_group') {
    return answeredYesNo(ans) ? { ...base, question_type: 'yes_no' } : null;
  }
  if (q.interaction_kind === 'most_least_group') {
    return answeredMostLeast(ans) ? { ...base, question_type: 'most_least' } : null;
  }
  if (isMultipleChoice(q)) {
    if (!ans.label && !ans.answer_id) return null;
    return {
      ...base,
      question_type: 'multiple_choice',
      selected_answer_letter: orNull(ans.label),
      selected_answer_id: orNull(ans.answer_id),
      correct_answer_letter: orNull(q.correct_answer),
    };
  }
  return hasText(ans) ? { ...base, question_type: 'text', text_response: String(ans.text_response) } : null;
}

/**
 * @param {Array} questions the quiz's questions, in order
 * @param {Object} userAnswers answers keyed by question.id
 * @param {{autoSubmit?: boolean|null, timeRemainingSeconds?: number|null}} options
 *   null when not known (a part completed from inside the Pearson player)
 */
export function buildQuizTracking(questions = [], userAnswers = {}, { autoSubmit = null, timeRemainingSeconds = null } = {}) {
  const answers = [];
  (questions || []).forEach((q, index) => {
    const ans = userAnswers[q.id];
    if (!ans) return;
    const tracked = trackedAnswer(q, ans, index + 1);
    if (tracked) answers.push(tracked);
  });
  return {
    answers,
    auto_submit: autoSubmit,
    time_remaining_seconds: Number.isFinite(timeRemainingSeconds) ? Math.max(0, Math.round(timeRemainingSeconds)) : null,
  };
}
